import { useEffect, useMemo, useState } from 'react';

export type UseClientPagedListOptions<T> = {
  items: T[];
  /**
   * Return true to keep the item. `normalizedSearch` is trim + lowercased and
   * is only passed when non-empty (empty search returns every item).
   */
  matchesSearch: (item: T, normalizedSearch: string) => boolean;
  pageSizeOptions: readonly number[];
  /**
   * When this value changes, `pageIndex` resets to 0 (in addition to search and
   * page size). Referral sources pass the selected clinic id.
   */
  scopeKey?: string | number | null;
};

/**
 * Client-side search + pagination for an already-loaded list: filtered rows,
 * safe page index, and the visible slice. Resets to page 0 when search, page
 * size, or `scopeKey` changes.
 */
export function useClientPagedList<T>({
  items,
  matchesSearch,
  pageSizeOptions,
  scopeKey,
}: UseClientPagedListOptions<T>) {
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter(item => matchesSearch(item, query));
  }, [items, search, matchesSearch]);

  useEffect(() => {
    setPageIndex(0);
  }, [search, pageSize, scopeKey]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  // Clamp the displayed page immediately if the filtered set shrank below the
  // current page (avoids one empty frame before the persist effect below).
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  // Persist that clamp in state — same as the former referral-sources list.
  // Masking alone leaves a stale index, so a later refresh that restores enough
  // rows would jump the user back to the old page.
  useEffect(() => {
    if (pageIndex > 0 && pageIndex > pageCount - 1) {
      setPageIndex(pageCount - 1);
    }
  }, [pageIndex, pageCount]);

  const visible = filtered.slice(
    safePageIndex * pageSize,
    safePageIndex * pageSize + pageSize
  );

  return {
    search,
    setSearch,
    pageSize,
    setPageSize,
    /** Clamped index for the footer and the visible slice. */
    pageIndex: safePageIndex,
    setPageIndex,
    pageCount,
    filtered,
    visible,
    canPreviousPage: safePageIndex > 0,
    canNextPage: safePageIndex < pageCount - 1,
  };
}
